import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, caseStudySchema } from "../lib/seo";

const BeHotelesCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies/behoteles")}
        schema={[
          caseStudySchema("/case-studies/behoteles"),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: "BeHoteles", path: "/case-studies/behoteles" },
        ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/case-studies"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
                data-testid="back-to-case-studies-link"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Case Studies
              </Link>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                BeHoteles: A No WIFI Solution
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage:
                    "url('/images/case-studies/behoteles.png')",
                }}
                role="img"
                aria-label="BeHoteles BeTulum New Year Celebration"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Overview</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Over the 2019 new year's celebration, several thousand guests were hosted over a two week period including the December 31st main celebration. BeTulum's event venue featured beachside entertainment and drink service in the remote jungles on the Mexican Caribbean. Once guests verified their tickets, BeHoteles wanted the wristbands to serve as a ticket for entry to the event.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Challenges</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              BeHoteles required a closed loop cashless solution that worked without Internet, accommodate the hotel's desired workflow and business processes, and most importantly prevent cash from being lost or stolen by temporary bar service staff. In such a remote and exotic location, WiFi connectivity could not be provided to support live event operations. BeHoteles needed a payment solution that would work flawlessly without Internet access.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Since BeHoteles wanted the wristbands to serve as a ticket, the wristbands were designed with clasps that were "one way", such that once fitted on, the guest could never be removed without cutting the strap and thereby invalidating the ticket. Not only did this prevent guests from losing their wristbands, it also prevented guests from giving or selling the wristband to another person to gain unauthorized access to the event.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Solution</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              WristCoin wristbands were printed with the event theme's branding and delivered to the customer ready to be used. Guests arrived at a central registration point where their tickets were validated, and wristbands were provided. The guest was then able to load the wristband with any amount of credit using either cash or credit.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Bartenders were each assigned unique staff identifiers to login to the mobile point of sale terminals and using WristCoin's flexible and powerful staff management system they were granted only the ability to deduct credit to enter drink orders. Cashiers that are hotel staff trusted to handle cash were granted the ability to add credit for guest top-ups but not deduct any credit.
            </p>

            <img
              src="/images/case-studies/wristcoin-cashless-payments.png"
              alt="WristCoin cashless payments at BeHoteles"
              className="w-full rounded-lg shadow-2xl mb-10"
            />

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Outcome</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Bar service proceeded quickly and smoothly irrespective of Internet or WiFi availability.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              After the event, all the terminals were synchronized with WristCoin's central server providing a rich set of statistics (figures redacted for customer privacy).
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              No cash lost or otherwise unaccounted for and all top-up totals were easily reconciled against cash deposits and credit card receipts after the event.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Guests' top-ups were nonrefundable at the request of WristCoin's customer (BeHoteles). In fact, this is an event configuration that is entirely customizable by the event organizers. The unspent top-ups were 20% of the total amount paid, representing alone an ROI on the investment in WristCoin.
            </p>

            <img
              src="/images/case-studies/wristcoin-dashboard.png"
              alt="WristCoin dashboard for BeHoteles"
              className="w-full rounded-lg shadow-2xl mb-10"
            />
          </article>
        </div>
      </section>
    </div>
  );
};

export default BeHotelesCaseStudy;
